import { ComponentType } from "react";
import {
    Dispatch, bindActionCreators, AnyAction, ActionCreatorsMapObject,
} from "redux";
import { connect, useDispatch, useSelector, shallowEqual } from "react-redux";
import { createSelector } from "reselect";

import { IStoreState } from "@montana/store/store";
import { IBaseContainerProps, generateBaseContainer } from "./base.container";

/**
 * @author SBF
 *
 * @description
 * The props rendered/exposed by the container component/HoC
 */
export type IContainerProps<TState, TActions = {}> = TState & TActions;

/**
 * @author SBF
 *
 * @description
 * Generates a redux container for a substate of the redux store.
 *
 * @param {IMapStateFn} mapStateFn state mapping function as normally used for connect in react-redux.
 * @param {Object} [mapActionsObject] key value pair of action creators and respective names.
 * @param {boolean} pure If true, implements shouldComponentUpdate and shallowly compares the result of mergeProps.
 *
 * @example
 * const mapState = (state: IStoreState): ISubState => state.subState;
 * const actions = {
 *     anAction: () => ({ type: "AN_ACTION" }),
 *     another: () =>  ({ type: "ANOTHER_ACTION" })
 * }
 *
 * const [SubstateContainer, connectWithSubstate, useSubstate] = generateContainer(mapState, actions);
 */
export function generateContainer<TSubState, TActionsMap extends ActionCreatorsMapObject<AnyAction>, TStoreState = IStoreState>(
    mapStateFn: (state: TStoreState) => TSubState,
    mapActionsObject: TActionsMap = {} as TActionsMap,
    pure = true,
    // eslint-disable-next-line no-undef
): [typeof Container, typeof connectWithContainer, () => TSubState & typeof mapActionsObject] {
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const mapActions = (dispatch: Dispatch) => bindActionCreators(mapActionsObject, dispatch);

    type TContainerProps = IContainerProps<TSubState, typeof mapActionsObject>;

    const selector = createSelector(
        mapStateFn,
        (substate) => substate,
    );

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    function connectWithContainer<TOwnProps = {}>(comp: ComponentType<TContainerProps & TOwnProps>) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (connect as any)(selector, mapActions, null, { pure })(comp as any);
    }

    const Container = connectWithContainer<IBaseContainerProps<TContainerProps>>(generateBaseContainer());

    function useContainer(): TSubState & typeof mapActionsObject {
        const dispatch = useDispatch();

        const state = useSelector(selector, shallowEqual);
        const actions = mapActions(dispatch);

        return { ...state, ...actions };
    }

    return [Container, connectWithContainer, useContainer];
}
